/**
 * Common Validation Service
 * Centralizes all validation logic used across the application.
 * Regex patterns are fetched from the API (GetMainFieldRegexData)
 * and used dynamically for field validation.
 */

import CommonObj from "../../CommonObj";
import { encryptTimestamp } from "../commoncsrf/csrf-common";

class ValidationService {
  constructor() {
    this.regexData = null;
  }

  async fetchRegexData(accessToken) {
    if (this.regexData) {
      return this.regexData;
    }

      try {
      const response = await fetch(CommonObj.Endpoints.GetMainFieldRegexData, {
        method: "GET",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-CSRF-Token": encryptTimestamp(),
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      this.regexData = await response.json();
      return this.regexData;
    } catch (error) {
      console.error("Failed to fetch regex data:", error);
      throw error;
    }
  }

  // ─── Helper: Get regex pattern from API data ───
  getPattern(fieldName) {
    if (!this.regexData || !this.regexData[fieldName]) return null;
    return this.regexData[fieldName][0]?.regexPattern || null;
  }

  // ─── Helper: Test value against API regex ───
  testPattern(fieldName, value) {
    const pattern = this.getPattern(fieldName);
    if (!pattern) return true; // No pattern loaded — skip regex check
    try {
      const regex = new RegExp(pattern, "u");
      return regex.test(value);
    } catch (e) {
      console.error(`Invalid regex for ${fieldName}:`, e);
      return true;
    }
  }

  // ─── Generic: Validate any field using API regex ───
  validateField(fieldName, value, requiredMsg) {
    if (this.isMissing(value)) {
      return { isValid: false, error: requiredMsg || `${fieldName} is required` };
    }
    if (!this.testPattern(fieldName, value)) {
      return { isValid: false, error: `Invalid format for ${fieldName}.` };
    }
    return { isValid: true, error: "" };
  }

  // ─── XSS Check (uses "XSS" pattern from API) ───
  hasXSS(value) {
    if (!value) return false;
    const pattern = this.getPattern("XSS");
    if (!pattern) return false;
    try {
      const regex = new RegExp(pattern, "i");
      return regex.test(value);
    } catch (e) {
      return false;
    }
  }

  // ─── BID ───
  validateBid(value) {
    if (!value) {
      return "Scan the BID number of the book.";
    }
    if (!this.testPattern("BID", value)) {
      return "The format should be 'BID' followed by 13 alphanumeric characters and a hyphen.";
    }
    if (value.length !== 16) {
      return "BID Value must be 16 chars (alphanumeric, hyphens only).";
    }
    if (!value.includes("-")) {
      return "BID Value must include at least one hyphen.";
    }
    return "";
  }

  validateRecycleBin(value) {
    if (!value || !value?.trim()) {
      return "Recycle Bin cannot be empty";
    }

    if (!this.testPattern("RecycleBin", value?.trim())) {
        return "Invalid Format: Format should be GL followed by 3 or 4 digits (e.g., GL XXX or GL XXXX)";
    }

    return "";
  }

  // ─── ISBN ───
  validateISBN(value) {
    if (!value || !value.trim()) {
      return { isValid: false, error: "Please Enter / Scan the ISBN number of the book" };
    }
    if (!this.testPattern("ISBN", value)) {
      return { isValid: false, error: "Please enter a valid 10 or 13-digit ISBN. (Note: Only numbers are allowed, except for an 'X' at the end of a 10-digit ISBN)." };
    }
    return { isValid: true, error: "" };
  }

  // ─── ISBN-10 ───
  validateISBN10(value) {
    if (!value || !value.trim()) {
      return { isValid: false, error: "ISBN-10 is required" };
    }
    if (!this.testPattern("ISBN10", value)) {
      return { isValid: false, error: "Invalid characters in ISBN-10." };
    }
    return { isValid: true, error: "" };
  }

  // ─── ISBN-13 ───
  validateISBN13(value) {
    if (!value || !value.trim()) {
      return { isValid: false, error: "ISBN-13 is required" };
    }
    if (!this.testPattern("ISBN13", value)) {
      return { isValid: false, error: "Invalid characters in ISBN-13." };
    }
    return { isValid: true, error: "" };
  }

  // ─── Title ───
  validateTitle(value) {
    if (this.isMissing(value)) return "Title is required";
    if (!this.testPattern("Title", value)) return "Invalid characters in Title.";
    return "";
  }

  // ─── Author ───
  validateAuthor(value) {
    if (this.isMissing(value)) return "Author is required";
    if (!this.testPattern("Author", value)) return "Invalid characters in Author.";
    return "";
  }

  // ─── Publisher ───
  validatePublisher(value) {
    if (this.isMissing(value)) return "Publisher is required";
    if (!this.testPattern("Publisher", value)) return "Invalid characters in Publisher.";
    return "";
  }

  // ─── Comments ───
  validateComments(value) {
    if (this.isMissing(value)) return "";  // Comments are optional
    if (this.hasXSS(value)) return "Invalid content detected in Comments.";
    if (!this.testPattern("Comments", value)) return "Invalid characters in Comments.";
    return "";
  }

  // ─── Name ───
  validateName(value) {
    if (this.isMissing(value)) return "Name is required";
    if (!this.testPattern("Name", value)) return "Invalid characters in Name. Only letters, spaces, dots, and hyphens allowed.";
    return "";
  }

  // ─── Page Count ───
  validatePageCount(value) {
    if (!value || Number(value) <= 0) {
      return { isValid: false, error: "Page count must be greater than 0" };
    }
    if (!this.testPattern("PageCount", value)) {
      return { isValid: false, error: "Page count must be whole number" };
    }
    return { isValid: true, error: "" };
  }
  
    // ─── Updated Page Count ───
    validateUpdatePageCount(value) {
        if (!value || Number(value) <= 0) {
            return { isValid: false, error: "Please enter the Updated page count" };
        }
    if (!this.testPattern("PageCount", value)) {
            return { isValid: false, error: "Please enter the whole number" };
        }
        return { isValid: true, error: "" };
    }
  validateWeight(value) {
    if (!value || (typeof value === "string" && !value.trim())) {
      return { isValid: false, error: "Weight is required" };
    }
    if (!this.testPattern("Weight", value.toString())) {
      return { isValid: false, error: "Please enter a valid number or decimal." };
    }
    return { isValid: true, error: "" };
  }

  // ─── Generic Required ───
  validateRequired(value, fieldName) {
    if (!value || (typeof value === "string" && !value.trim())) {
      return `${fieldName} is required`;
    }
    return "";
  }

  // ─── Helper: isMissing ───
  isMissing(value) {
    return !value || (typeof value === "string" && !value.trim());
  }

  // ─── Language ───
  validateLanguage(value) {
    return this.validateRequired(value, "Language");
  }

  // ─── Condition ───
  validateCondition(value) {
    return this.validateRequired(value, "Condition");
  }

  // ─── Book Type (Binding) ───
  validateBookType(value) {
    return this.validateRequired(value, "Binding");
  }

  // ─── Book Format ───
  validateBookFormat(value) {
    return this.validateRequired(value, "Book Type");
  }

  // ─── Content ───
  validateContent(value) {
    if (!Array.isArray(value) || value.length === 0) {
      return "At least one content type must be selected";
    }
    return "";
  }

  // ─── Used Condition (Wear Level) ───
  validateUsedCondition(value, condition) {
    if (condition === "Used Condition" && (!value || !value.trim())) {
      return "Wear Level is required";
    }
    return "";
  }

  // ─── EQ-Complexity Level ───
  validateEQComplexityLevel(value, bookFormat) {
    if (bookFormat !== "Exceptions" && (!value || !value.trim())) {
      return "EQ-Complexity Level is required";
    }
    return "";
  }

  // ─── Format Exception ───
  validateFormatException(value, bookFormat) {
    if (bookFormat === "Exceptions" && !value) {
      return "Reason for exception is required";
    }
    return "";
  }

  // ─── Rack and Shelf ───
  validateRackAndShelf(value) {
    return this.validateRequired(value, "Rack and Shelf");
  }

  clearCache() {
    this.regexData = null;
  }
}

export default new ValidationService();
